/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/AddBreakfastPartners/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useRef, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Select, { components as selectComponents } from "react-select";

import styles from "./index.module.css";

import { getSectors } from "../../logic/sectors";
import { countBreakfastEventCompanies } from "../../logic/breakfastEvents/index.js";
import { getCompanySizeOptions } from "../../data/selectOptions";

/** Keeps react-select menus from growing the dialog layout / scroll area */
const selectModalMenuProps = {
  menuPosition: "fixed",
  menuShouldScrollIntoView: false,
};

const mergeSubSectorsFromSectors = (sectors, sectorIds) => {
  const map = new Map();
  for (const sid of sectorIds) {
    const sec = sectors.find((s) => s._id === sid);
    if (sec?.subSectores) {
      for (const sub of sec.subSectores) {
        map.set(sub._id, sub);
      }
    }
  }
  return Array.from(map.values());
};

const mergeBranchesFromSubsectors = (subSectorRows, subSectorIds) => {
  const map = new Map();
  for (const subId of subSectorIds) {
    const sub = subSectorRows.find((s) => s._id === subId);
    if (sub?.ramas) {
      for (const r of sub.ramas) {
        map.set(r._id, r);
      }
    }
  }
  return Array.from(map.values());
};

const MAX_MULTI_CHIPS_VISIBLE = 2;

/** Renders at most two value chips; remaining count as "N+" (e.g. 3+). */
const LimitedMultiValue = (props) => {
  const { index } = props;
  const total = props.selectProps.value?.length ?? 0;

  if (index < MAX_MULTI_CHIPS_VISIBLE) {
    return <selectComponents.MultiValue {...props} />;
  }
  if (index === MAX_MULTI_CHIPS_VISIBLE && total > MAX_MULTI_CHIPS_VISIBLE) {
    const extra = total - MAX_MULTI_CHIPS_VISIBLE;
    return <span className={styles.multiValueOverflow}>{extra}+</span>;
  }
  return null;
};

const limitedMultiSelectComponents = {
  MultiValue: LimitedMultiValue,
};

const emptyPartnerForm = () => ({
  audiencia: null,
  tamanosDeLaEmpresa: [],
  sectores: [],
  subSectores: [],
  ramas: [],
});

/** API may return id strings or `{ _id }` documents. */
const normalizeIdList = (value) => {
  if (value == null) {
    return [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  const out = [];
  for (const item of value) {
    if (item == null) {
      continue;
    }
    const id =
      typeof item === "object" && item !== null && "_id" in item
        ? item._id
        : item;
    if (id != null && id !== "") {
      out.push(id);
    }
  }
  return out;
};

/** Company size values may be scalar or `{ value }` from selects. */
const normalizeCompanySizeList = (value) => {
  if (value == null) {
    return [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (item == null) {
        return null;
      }
      if (typeof item === "object" && item !== null && "value" in item) {
        return item.value;
      }
      return item;
    })
    .filter((v) => v !== null && v !== "");
};

const partnerFormFromEventData = (eventData) => {
  logger.info("eventData: ", { data: eventData });
  if (!eventData) {
    return emptyPartnerForm();
  }
  return {
    audiencia: eventData.audiencia,
    tamanosDeLaEmpresa: normalizeCompanySizeList(eventData.tamanosDeLaEmpresa),
    sectores: normalizeIdList(eventData.sectores),
    subSectores: normalizeIdList(eventData.subSectores),
    ramas: normalizeIdList(eventData.ramas),
  };
};

const AddBreakfastPartners = ({ open, eventData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dialogRef = useRef(null);
  const [sectors, setSectors] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [audienceCount, setAudienceCount] = useState(null);
  const [isCountingAudience, setIsCountingAudience] = useState(false);
  const [showError, setShowError] = useState(false);
  const requestRef = useRef(0);

  const companySizeOptions = getCompanySizeOptions(t);

  const [formData, setFormData] = useState(() => emptyPartnerForm());

  const subSectorOptions = useMemo(
    () => mergeSubSectorsFromSectors(sectors, formData.sectores ?? []),
    [sectors, formData.sectores],
  );

  const branchOptions = useMemo(
    () =>
      mergeBranchesFromSubsectors(subSectorOptions, formData.subSectores ?? []),
    [subSectorOptions, formData.subSectores],
  );

  // Effect for dialog management only
  useEffect(() => {
    const handleClose = () => {
      onCancel();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      // Check if dialog is already open before trying to open it
      if (dialog && !dialog.open) {
        dialog.showModal();
      }
    } else {
      // Only close if dialog is currently open
      if (dialog && dialog.open) {
        dialog.close();
      }
      // Reset state when modal closes
      setIsInitialized(false);
      setIsEditMode(false);
      setFormData(emptyPartnerForm());

      setAudienceCount(null);
      setIsCountingAudience(false);
      requestRef.current = 0;
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onCancel]);

  // Effect for data fetching - only runs when modal opens and hasn't been initialized
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const allSectors = await getSectors(navigate);
        setSectors(allSectors);
        setIsInitialized(true);
      } catch (error) {
        logger.error("Error fetching sectors:", { err: sanitizeError(error) });
      }
    };

    if (open && !isInitialized) {
      fetchSectors();
    }
  }, [open, isInitialized, navigate]);

  // After sectors load, prefill from the breakfast event (edit) or leave empty (create).
  useEffect(() => {
    if (!open || !isInitialized) {
      return;
    }
    setFormData(partnerFormFromEventData(eventData));
    setIsEditMode(Boolean(eventData?._id));
  }, [open, isInitialized, eventData]);

  const checkSaveDisabled = () => {
    // Empty form is allowed; if any sector is selected, at least one sub-sector is required.
    const hasSectors = (formData.sectores ?? []).length > 0;
    const hasSubSectors = (formData.subSectores ?? []).length > 0;
    return hasSectors && !hasSubSectors;
  };

  // Effect for audience count
  useEffect(() => {
    if (!open || !isInitialized) {
      return;
    }

    if (formData.audiencia == null) {
      setAudienceCount(0);
      return;
    }

    const timeout = setTimeout(async () => {
      const requestId = ++requestRef.current;
      try {
        setIsCountingAudience(true);
        const total = await countBreakfastEventCompanies(
          {
            audiencia: formData.audiencia,
            tamanosDeLaEmpresa: formData.tamanosDeLaEmpresa,
            sectores: formData.sectores,
            subSectores: formData.subSectores,
            ramas: formData.ramas,
          },
          navigate,
        );
        if (requestId !== requestRef.current) {
          return;
        }
        setAudienceCount(total);
      } catch (error) {
        logger.error("Error counting companies:", {
          err: sanitizeError(error),
        });
      } finally {
        if (requestId === requestRef.current) {
          setIsCountingAudience(false);
        }
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [
    open,
    isInitialized,
    formData.tamanosDeLaEmpresa,
    formData.sectores,
    formData.subSectores,
    formData.ramas,
    formData.audiencia,
    navigate,
  ]);

  const onSubmit = () => {
    onSuccess(formData, isEditMode);
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}

      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>
            {t("components.addBreakfastPartnersModal.title")}
          </label>

          <div className={styles.inputGroup}>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addBreakfastPartnersModal.companySize")}
              </label>
              <Select
                {...selectModalMenuProps}
                components={limitedMultiSelectComponents}
                isMulti
                options={companySizeOptions}
                value={companySizeOptions.filter((o) =>
                  (formData.tamanosDeLaEmpresa ?? []).some(
                    (v) => v === o.value || Number(v) === Number(o.value),
                  ),
                )}
                isClearable
                isSearchable={false}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={(selectedOptions) => {
                  setFormData((prev) => ({
                    ...prev,
                    tamanosDeLaEmpresa:
                      selectedOptions?.map((o) => o.value) ?? [],
                  }));
                }}
                placeholder={t(
                  "components.addBreakfastPartnersModal.companySizePlaceholder",
                )}
                maxMenuHeight={100}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    minHeight: "40px",
                    height: "auto",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    flexWrap: "wrap",
                    paddingTop: 4,
                    paddingBottom: 4,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
              />
            </div>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addBreakfastPartnersModal.sector")}
              </label>
              <Select
                {...selectModalMenuProps}
                components={limitedMultiSelectComponents}
                isMulti
                options={sectors}
                value={sectors.filter((s) =>
                  (formData.sectores ?? []).includes(s._id),
                )}
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={(selectedOptions) => {
                  const sectorIds = selectedOptions?.map((o) => o._id) ?? [];
                  setFormData((prev) => {
                    const nextSubOpts = mergeSubSectorsFromSectors(
                      sectors,
                      sectorIds,
                    );
                    const validSubIds = new Set(nextSubOpts.map((s) => s._id));
                    const keptSubIds = (prev.subSectores ?? []).filter((id) =>
                      validSubIds.has(id),
                    );
                    const nextBranchOpts = mergeBranchesFromSubsectors(
                      nextSubOpts,
                      keptSubIds,
                    );
                    const validRamaIds = new Set(
                      nextBranchOpts.map((b) => b._id),
                    );
                    return {
                      ...prev,
                      sectores: sectorIds,
                      subSectores: keptSubIds,
                      ramas: (prev.ramas ?? []).filter((id) =>
                        validRamaIds.has(id),
                      ),
                    };
                  });
                }}
                placeholder={t(
                  "components.addBreakfastPartnersModal.sectorPlaceholder",
                )}
                maxMenuHeight={120}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    borderColor:
                      showError && !(formData.sectores ?? []).length
                        ? "red"
                        : "#909092",
                    width: "100%",
                    minHeight: "36px",
                    height: "auto",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    maxWidth: "100%",
                    flexWrap: "wrap",
                    paddingTop: 4,
                    paddingBottom: 4,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addBreakfastPartnersModal.subsector")}
              </label>
              <Select
                {...selectModalMenuProps}
                components={limitedMultiSelectComponents}
                isMulti
                options={subSectorOptions}
                value={subSectorOptions.filter((s) =>
                  (formData.subSectores ?? []).includes(s._id),
                )}
                isSearchable={false}
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={(selectedOptions) => {
                  const subIds = selectedOptions?.map((o) => o._id) ?? [];
                  const nextBranchOpts = mergeBranchesFromSubsectors(
                    subSectorOptions,
                    subIds,
                  );
                  const validRamaIds = new Set(
                    nextBranchOpts.map((b) => b._id),
                  );
                  setFormData((prev) => ({
                    ...prev,
                    subSectores: subIds,
                    ramas: (prev.ramas ?? []).filter((id) =>
                      validRamaIds.has(id),
                    ),
                  }));
                }}
                maxMenuHeight={120}
                placeholder={t(
                  "components.addBreakfastPartnersModal.subsectorPlaceholder",
                )}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    minHeight: "40px",
                    height: "auto",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    flexWrap: "wrap",
                    paddingTop: 4,
                    paddingBottom: 4,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.addBreakfastPartnersModal.economicActivity")}
              </label>
              <Select
                {...selectModalMenuProps}
                components={limitedMultiSelectComponents}
                isMulti
                options={branchOptions}
                value={branchOptions.filter((b) =>
                  (formData.ramas ?? []).includes(b._id),
                )}
                isSearchable={false}
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                onChange={(selectedOptions) => {
                  setFormData((prev) => ({
                    ...prev,
                    ramas: selectedOptions?.map((o) => o._id) ?? [],
                  }));
                }}
                placeholder={t(
                  "components.addBreakfastPartnersModal.economicActivityPlaceholder",
                )}
                maxMenuHeight={100}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    borderColor:
                      showError && !(formData.ramas ?? []).length
                        ? "red"
                        : "#909092",
                    width: "100%",
                    minHeight: "36px",
                    height: "auto",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    maxWidth: "100%",
                    flexWrap: "wrap",
                    paddingTop: 4,
                    paddingBottom: 4,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
          </div>
        </div>
        <div className={styles.audienceCountContainer}>
          {isCountingAudience ? (
            <span>
              {t("components.addBreakfastPartnersModal.calculatingCompanies")}
            </span>
          ) : (
            <span>
              {t("components.addBreakfastPartnersModal.companiesFound", {
                count: audienceCount ?? 0,
              })}
            </span>
          )}
        </div>

        <div className={styles.modalButtons}>
          <button
            type="button"
            className={styles.confirmButton}
            disabled={checkSaveDisabled()}
            onClick={() => onSubmit()}
          >
            {t("components.addBreakfastPartnersModal.addButton")}
          </button>
        </div>
      </dialog>
      {/* {open && ()} */}
    </>
  );
};

export default AddBreakfastPartners;
